import { useSearchParams } from 'next/navigation';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { Modals } from '@/helpers/modals';
import { ReceiveBonusesNotice } from '@/modules/common';
import { scrollToPromoCodeInput } from '@/modules/promotions/helpers/promocode';
import { useGetCommonStore, useModalsStore } from '@/store';
import { CouponPrefilledNotice } from './components/CouponPrefilledNotice';
import { InputPromo } from './components/InputPromo';
import { DISTANCE_TO_TOP, PROMOCODE_SECTION_QUERY } from './helpers';
import { usePrefilledNdbCoupon } from './hooks/usePrefilledNdbCoupon';
import { ScHavePromocode, ScPromocodeFormWrapper } from './styled';

export interface IHavePromocodeSectionProps {
  onPromocodeActivationSuccess: () => void;
  isLoading?: boolean;
}

export const HavePromocodeSection: FC<IHavePromocodeSectionProps> = ({
  onPromocodeActivationSuccess,
  isLoading,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation(['bonuses', 'promotions']);

  const searchParams = useSearchParams();
  const useCommonStore = useGetCommonStore();
  const open = useModalsStore.use.open();

  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const { isPrefilledNdbCoupon, coupon } = usePrefilledNdbCoupon();

  const [isReceiveBonusesNoticeVisible, setIsReceiveBonusesNoticeVisible] =
    useState(false);

  const shouldShowPrefilledNdbCoupon =
    isPrefilledNdbCoupon && !isReceiveBonusesNoticeVisible;

  const handleRegisterClick = useCallback(() => {
    open(Modals.Registration);
  }, [open]);

  const handleVisibilityChange = useCallback((isVisible: boolean) => {
    setIsReceiveBonusesNoticeVisible(isVisible);
  }, []);

  const descriptionText = useMemo(() => {
    if (!isAuthenticated) return t('bonuses:claimBonus.nonAuth.description');
    return t('bonuses:claimBonus.description');
  }, [isAuthenticated, t]);

  useEffect(() => {
    if (isLoading) return;
    const block = searchParams.get('block');

    const timeoutId = setTimeout(() => {
      if (block === PROMOCODE_SECTION_QUERY) {
        scrollToPromoCodeInput(ref, DISTANCE_TO_TOP, 'smooth');
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchParams.get('block'), isLoading]);

  return (
    <ScHavePromocode ref={ref}>
      <div className="header">
        <div className="title">{t('bonuses:claimBonus.title')}</div>
        <div className="description">{descriptionText}</div>
      </div>

      <ScPromocodeFormWrapper>
        <div className="bonuses-notice-box">
          <ReceiveBonusesNotice
            onVisibilityChange={handleVisibilityChange}
            successMessage="canIssueBonuses.message.success"
            translation="bonuses"
            warningMessage="canIssueBonuses.message.warning"
          />
        </div>
        {isAuthenticated && (
          <>
            {shouldShowPrefilledNdbCoupon && <CouponPrefilledNotice />}
            <InputPromo
              ndbCoupon={shouldShowPrefilledNdbCoupon ? coupon : undefined}
              onPromocodeActivationSuccess={onPromocodeActivationSuccess}
            />
          </>
        )}
        {!isAuthenticated && (
          <Button
            className="join-btn"
            onClick={handleRegisterClick}
            primary
            small
            wide
          >
            {t('common:button.joinNow')}
          </Button>
        )}
      </ScPromocodeFormWrapper>
    </ScHavePromocode>
  );
};
